/**
 * i18n.ts — the `createI18n` factory: a translator + locale store + Intl
 * formatters over one source catalog. Framework-agnostic and instantiable
 * (no module-level singleton), so web-kit VMs and a React adapter each own a
 * scoped instance and tests run in isolation.
 */
import type { Catalog, Locale, LocaleOverrides, MessageValue, PluralForms, Vars } from "./types.ts";
import { createLocaleStore, type LocaleStore } from "./store.ts";
import { createFormatters, type LocaleFormatters } from "./format.ts";

export interface I18nConfig<C extends Catalog> {
  /**
   * The source catalog: English key → English default text. Its keys are the
   * typed surface of `t`. Pass an object literal to infer the exact key union.
   */
  catalog: C;
  /**
   * Per-locale override maps, keyed by BCP-47 tag (e.g. `"de-DE"`). Each is a
   * subset of the catalog's keys; omitted keys fall back to the English default.
   */
  locales?: Record<Locale, LocaleOverrides<C>>;
  /** The locale the store starts on. German-first default: `"de-DE"`. */
  initialLocale?: Locale;
}

export interface I18n<C extends Catalog> {
  /** The observable active-locale store. */
  readonly locale: LocaleStore;
  /** `Intl`-based formatters bound to the active locale. */
  readonly format: LocaleFormatters;
  /**
   * Translate a catalog key in the active locale, interpolating `{var}`
   * placeholders. Resolution: active-locale override → primary-subtag override
   * → English default. A wrong key is a compile error (`keyof C`).
   */
  t(key: keyof C, vars?: Vars): string;
  /** Convenience: subscribe to locale changes (delegates to `locale.subscribe`). */
  subscribe(listener: (locale: Locale) => void): () => void;
}

const DEFAULT_LOCALE = "de-DE";

/** The primary language subtag of a BCP-47 tag: `"de"` for `"de-DE"`. */
function primarySubtag(locale: Locale): string {
  const dash = locale.indexOf("-");
  return dash === -1 ? locale : locale.slice(0, dash);
}

/** Replace `{name}` placeholders; an unmatched placeholder is left verbatim. */
function interpolate(message: string, vars?: Vars): string {
  if (!vars) return message;
  return message.replace(/\{(\w+)\}/g, (_, name: string) =>
    name in vars ? String(vars[name]) : `{${name}}`,
  );
}

/** True when a message value carries plural forms (has a required `other`). */
function isPluralForms(value: MessageValue): value is PluralForms {
  return typeof value === "object" && value !== null && typeof (value as PluralForms).other === "string";
}

/**
 * Select the plural form for `count` on `locale` via `Intl.PluralRules`
 * (i18n-ADR I2 rule 5), falling back to `other` for any category the forms omit.
 * `PluralRules` instances are cached per locale.
 */
function selectPlural(
  forms: PluralForms,
  count: number,
  locale: Locale,
  cache: Map<Locale, Intl.PluralRules>,
): string {
  let rules = cache.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    cache.set(locale, rules);
  }
  const category = rules.select(count) as keyof PluralForms;
  return forms[category] ?? forms.other;
}

/** The plural count for a message: `vars.count` wins, else `vars.n` (both numeric). */
function pluralCount(vars?: Vars): number | undefined {
  const raw = vars?.count ?? vars?.n;
  return typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() !== "" && !Number.isNaN(Number(raw)) ? Number(raw) : undefined;
}

export function createI18n<C extends Catalog>(config: I18nConfig<C>): I18n<C> {
  const { catalog, locales = {}, initialLocale = DEFAULT_LOCALE } = config;
  const store = createLocaleStore(initialLocale);
  const format = createFormatters(store.get);
  const pluralCache = new Map<Locale, Intl.PluralRules>();

  function overridesFor(locale: Locale): LocaleOverrides<C> | undefined {
    return locales[locale] ?? locales[primarySubtag(locale)];
  }

  function t(key: keyof C, vars?: Vars): string {
    const locale = store.get();
    const override = overridesFor(locale);
    const localized = override?.[key];
    // The English default is the guaranteed fallback (no orphan keys); the
    // `?? String(key)` is an unreachable runtime guard for untyped callers.
    const message: MessageValue = localized ?? catalog[key] ?? String(key);
    if (isPluralForms(message)) {
      const count = pluralCount(vars);
      const form = count === undefined ? message.other : selectPlural(message, count, locale, pluralCache);
      return interpolate(form, vars);
    }
    return interpolate(message, vars);
  }

  return {
    locale: store,
    format,
    t,
    subscribe: store.subscribe,
  };
}
